/**
 * IDP-983: Live per-environment status for a microapp, from
 * /api/microapps-api/environment-status/{appName} (backed by ArgoCD).
 *
 * Shared by the admin-only staging affordances (Launch · Staging, staging
 * ArgoCD card) and the Environments widget so "is staging live?" is always a
 * live check rather than a possibly-stale annotation.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  useApi,
  identityApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

export interface MicroappEnvState {
  deployed: boolean;
  commit: string | null;
  health: string | null;
  sync: string | null;
  url: string | null;
  /** IDP-983: true when environment is scaled to zero (e.g. after promote) */
  scaledToZero?: boolean;
}

export interface MicroappEnvironmentStatus {
  production: MicroappEnvState;
  staging: MicroappEnvState;
}

/**
 * IDP-983: Optimistic staging teardown.
 *
 * Teardown deletes the staging ArgoCD app + all K8s resources in the background,
 * which takes time — the backend /environment-status keeps reporting staging as
 * deployed until ArgoCD finishes. To give immediate feedback we set a
 * short-lived sessionStorage flag when teardown is initiated and force staging
 * to look removed until the server itself confirms staging is gone (at which
 * point the flag is cleared). A window event lets sibling consumers (e.g. the
 * staging ArgoCD card) refresh instantly rather than on the next poll/reload.
 */
const TEARDOWN_EVENT = 'microapp:staging-teardown';
const PROMOTE_EVENT = 'microapp:promote-complete';
const teardownKey = (appName: string) => `microapp-staging-teardown-${appName}`;

export function markStagingTeardownInProgress(appName: string): void {
  try {
    sessionStorage.setItem(teardownKey(appName), Date.now().toString());
  } catch {
    // sessionStorage unavailable — the initiating component still hides staging
    // via its own local state; only cross-component sync is affected.
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(TEARDOWN_EVENT, { detail: { appName } }),
    );
  }
}

function isStagingTeardownInProgress(appName: string): boolean {
  try {
    return !!sessionStorage.getItem(teardownKey(appName));
  } catch {
    return false;
  }
}

export function clearStagingTeardown(appName: string): void {
  try {
    sessionStorage.removeItem(teardownKey(appName));
  } catch {
    // ignore
  }
}

/**
 * Notify all useMicroappEnvironmentStatus consumers to refresh after promote.
 * This ensures the ArgoCD cards update when production becomes live.
 */
export function notifyPromoteComplete(appName: string): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(PROMOTE_EVENT, { detail: { appName } }),
    );
  }
}

/**
 * Force staging to look torn-down while a teardown is in progress. Once the
 * server reports staging is actually gone, the flag is cleared and the real
 * status is returned unchanged.
 */
export function applyStagingTeardownOverride(
  appName: string,
  status: MicroappEnvironmentStatus | null,
): MicroappEnvironmentStatus | null {
  if (!status || !isStagingTeardownInProgress(appName)) return status;

  // Server confirms staging is actually gone → teardown complete, clear flag.
  if (!status.staging?.deployed) {
    clearStagingTeardown(appName);
    return status;
  }

  // Teardown still converging on the backend — optimistically present staging as
  // removed so the URL, Teardown button and staging ArgoCD card update now.
  return {
    ...status,
    staging: {
      deployed: false,
      commit: null,
      health: null,
      sync: null,
      url: null,
    },
  };
}

export function useMicroappEnvironmentStatus(appName: string): {
  status: MicroappEnvironmentStatus | null;
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [status, setStatus] = useState<MicroappEnvironmentStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { token } = await identityApi.getCredentials();
      const response = await fetchApi.fetch(
        `/api/microapps-api/environment-status/${appName}`,
        {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setStatus(
          applyStagingTeardownOverride(appName, {
            production: data.production,
            staging: data.staging,
          }),
        );
      }
    } catch {
      // Non-fatal — leave status null; callers fall back to "unknown".
    } finally {
      setLoading(false);
    }
  }, [appName, identityApi, fetchApi]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Refresh when a teardown or promote completes elsewhere (e.g. the Environments card)
  // so this consumer (e.g. ArgoCD card) picks up the new status immediately.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { appName?: string }
        | undefined;
      if (!detail || detail.appName === appName) {
        void refresh();
      }
    };
    window.addEventListener(TEARDOWN_EVENT, handler);
    window.addEventListener(PROMOTE_EVENT, handler);
    return () => {
      window.removeEventListener(TEARDOWN_EVENT, handler);
      window.removeEventListener(PROMOTE_EVENT, handler);
    };
  }, [appName, refresh]);

  return { status, loading, refresh };
}
